import type { CourseQuizContent } from '../types'

export const strategicCommunicationContent: CourseQuizContent = {
  courseSlug:
    'strategic-communication-build-trust-drive-decisions-lead-with-influence',
  courseName: 'Strategic Communication',

  goalQuestion: {
    title: 'Which sounds most like you in a high-stakes meeting?',
    options: [
      {
        label: 'I’m the quiet one. I say less than I should.',
        value: 'quiet-one',
      },
      {
        label: 'I speak up, but my ideas don’t always land.',
        value: 'ideas-dont-land',
      },
      {
        label: 'I communicate well, and I want to win over tougher rooms.',
        value: 'harder-rooms',
      },
    ],
  },

  levelQuestion: {
    title: 'What’s your biggest communication goal at work right now?',
    options: [
      {
        label: 'Getting my ideas heard, backed, and credited.',
        value: 'move-ideas',
      },
      {
        label: 'Winning a yes for a pitch, proposal, or budget on the line.',
        value: 'win-a-yes',
      },
      {
        label: 'Staying steady under pressure: tough rooms, pushback, nerves.',
        value: 'under-pressure',
      },
      {
        label: 'Building trust and presence that grow over time.',
        value: 'trust-presence',
      },
    ],
  },

  primaryQuestionSlot: 'level',

  interstitial1: {
    pill: 'IN THIS COURSE',
    headline:
      'Every module is built around one real decision you need to influence.',
    body: 'Build your story, structure, empathy plan, and objection map, then pressure-test it all with AI roleplay. You’ll finish with a Strategic Influence Playbook.',
    image: 'https://static.masterclass.com/stratcomm-interstitial-1.png',
    imageAlt: 'The Strategic Influence Playbook course materials',
  },

  interstitial2: {
    pill: 'DID YOU KNOW',
    headline:
      'Being influential isn’t actually a personality type. It’s a skill you build.',
    body: 'Trust, presence, and handling pressure are all learnable skills. Influence is a system you practice, not a trait you’re born with.',
    image: 'https://static.masterclass.com/stratcomm-interstitial-2.png',
    imageAlt: 'A professional presenting to colleagues',
  },

  profiles: [
    {
      key: 'move-ideas',
      headline: 'You want your ideas heard, backed, and credited',
      lead: 'Your ideas are good, but the delivery isn’t. This course trains you to make strong first impressions and tell decision-ready stories, so your work is hard to ignore and you get the credit.',
      bullets: [
        {
          text: '**Command attention in the first seconds** with science-backed verbal and nonverbal cues.',
        },
        {
          text: '**Craft decision-ready stories** (ethos, pathos, logos) that make your idea the easy yes.',
        },
        {
          text: '**Build a first-moments plan** for the exact interaction where you need to land.',
        },
      ],
    },
    {
      key: 'win-a-yes',
      headline: 'You want to walk in prepared, and walk out with the yes',
      lead: 'There’s a specific yes you need to win, and good-enough communication won’t get it. This course has you arrive with every objection mapped and your pitch already pressure-tested.',
      bullets: [
        {
          text: 'Play **Boardroom Bingo** to anticipate the questions, objections, and scenarios before the room does.',
        },
        {
          text: '**Diagnose hesitation** (risk, misalignment, ambiguity, incentives) and adapt in real time.',
        },
        {
          text: '**Pressure-test your pitch in the Pitch Lab** with AI roleplay before it counts.',
        },
      ],
    },
    {
      key: 'under-pressure',
      headline: 'You want to stay sharp, even when the pressure is on',
      lead: 'You know your stuff. But some rooms are harder to work than others. Freezing under pressure isn’t a character flaw, but it is something you can avoid and plan for through this course.',
      bullets: [
        {
          text: 'Build your **Anxiety Management Plan**: map your triggers and manage pressure in real time.',
        },
        {
          text: '**Turn pushback into information** by diagnosing what’s driving the resistance instead of absorbing it.',
        },
        {
          text: '**Rehearse the hard moments with AI roleplay**, getting private reps before the room.',
        },
      ],
    },
    {
      key: 'trust-presence',
      headline: 'You want to become the voice the room trusts',
      lead: 'You’re playing the long game: building trust and presence that grow with every room. This course gives you an empathy-first system that makes credibility deliberate, not accidental.',
      bullets: [
        {
          text: '**Practice attunement**: uncover what stakeholders are actually weighing before you speak.',
        },
        {
          text: 'Design **audience-centered messages** with the Know-Feel-Do framework.',
        },
        {
          text: 'Build a repeatable system for **prep, rehearsal, and follow-through** that compounds credibility over time.',
        },
      ],
    },
  ],

  paceNotes: [
    { key: 'lt1h', weeks: '15 to 18 weeks' },
    { key: '1to3h', weeks: '6 to 8 weeks' },
    { key: '3to5h', weeks: '3 to 4 weeks' },
    { key: '5plush', weeks: '2 to 3 weeks' },
  ],

  previewLessons: [
    {
      title: 'Overcoming Objection and Hesitation',
      mediaUuid: 'c79a6cc4-a058-4cc9-a0bf-6e7ed35fe885',
      image: 'https://static.masterclass.com/stratcomm-video-1.png',
    },
    {
      title: 'Reading People: The Cues That Matter',
      mediaUuid: '204cb66b-e161-4e04-a29f-e2bb17dc1837',
      image: 'https://static.masterclass.com/stratcomm-video-2.png',
    },
  ],

  ctaHighlights: [
    'Learn from the best in the field.',
    'Create your Strategic Influence Playbook.',
    'Earn a shareable MasterClass Certificate.',
  ],
}
